import React from "react";
import { Layout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { useListings } from "@/hooks/useListings";
import { useSave } from "@/hooks/useSave";
import { Bookmark, Tag, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const Fashion = () => {
  const { save } = useSave();
  const { data: brands = [] } = useListings("fashion");

  return (
    <Layout>
      <PageHeader eyebrow="Fashion" title="Shop top brands, exclusive deals." subtitle="Member-only access to the labels and edits our stylists love." image="https://images.unsplash.com/photo-1664076458686-3449062080ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />

      {/* Shop centers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16" data-testid="shop-centers">
        <h2 className="font-serif text-2xl sm:text-3xl text-white mb-6">Order from anywhere</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {[
            { to: "/temu", name: "Temu Center", desc: "Paste any Temu link — we order it for you.", img: "https://images.pexels.com/photos/5868722/pexels-photo-5868722.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=900" },
            { to: "/shein", name: "Shein Center", desc: "Trend-led Shein fashion, ordered through MOVA.", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
          ].map((c) => (
            <Link to={c.to} key={c.to} data-testid={`shop-center-link-${c.to.slice(1)}`} className="group relative rounded-3xl overflow-hidden border border-amber-500/20 min-h-[220px] hover:border-amber-400/50 transition-colors duration-500">
              <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-[#08080c]/60 to-transparent" />
              <div className="relative p-8 h-full flex flex-col justify-end">
                <div className="w-11 h-11 rounded-full glass gold-border flex items-center justify-center text-champagne mb-4"><ShoppingBag size={20} /></div>
                <h3 className="font-serif text-2xl text-white">{c.name}</h3>
                <p className="text-slate-300 mt-2 text-sm">{c.desc}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm text-champagne group-hover:gap-3 transition-all duration-200">Open center <ArrowRight size={16} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="fashion-grid">
        <h2 className="font-serif text-2xl sm:text-3xl text-white mb-6">Featured brands</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {brands.map((b) => (
            <div key={b.id} data-testid={`brand-card-${b.id}`} className="group rounded-2xl overflow-hidden border border-white/10 hover:border-amber-400/40 transition-colors duration-500 bg-[#101017]">
              <div className="relative h-80 overflow-hidden">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {b.price && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass text-xs text-champagne gold-border"><Tag size={12} /> {b.price}</span>}
                <button onClick={() => save({ item_type: "fashion", title: b.title, subtitle: b.subtitle, image: b.image, meta: { deal: b.price } })} data-testid={`save-brand-${b.id}`} className="absolute top-4 right-4 w-9 h-9 rounded-full glass gold-border flex items-center justify-center text-champagne hover:bg-amber-400/20 transition-colors duration-200"><Bookmark size={15} /></button>
              </div>
              <div className="p-6 flex items-center justify-between">
                <div><h3 className="font-serif text-lg text-white">{b.title}</h3><p className="text-xs text-slate-400 mt-1">{b.subtitle}</p></div>
                <button onClick={() => toast.success("Added to your edit", { description: b.title })} data-testid={`shop-brand-${b.id}`} className="text-sm px-4 py-2 rounded-full border border-amber-400/30 text-slate-200 hover:bg-amber-400/10 transition-colors duration-200">Shop</button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
};

export default Fashion;
